/**
 * Call campaigns dashboard
 *
 * The Live card (who is on a call now, calls / reached / outcomes today, callers on shift; live from the Dashboards
 * hub, REST every minute when it is down), KPI tiles (active campaigns, open and unassigned people, calls today and this week, reach rate, open handoffs,
 * pending detail changes, lists waiting for review), the admin's reminders, progress of the active campaigns,
 * the journey funnel (DA, E1 to E5), top callers, outcomes this week and calls per day for the last 14 days.
 * Stage 6: "Themes from recent calls" (AI insights) when the insights flag is on and the admin may use AI.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconPhoneCall,
  IconUsers,
  IconUserQuestion,
  IconPhone,
  IconTargetArrow,
  IconArrowForwardUp,
  IconListDetails,
  IconFileImport,
  IconRefresh,
  IconPlus,
  IconChevronRight,
  IconTrophy,
  IconClock,
} from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Stack, Group, Button, Text, SimpleGrid, Grid, Alert, Paper, Skeleton, Badge } from '@/components/ui';
import ActionIcon from '@/components/ui/ActionIcon';
import { FadeIn } from '@/components/animations';
import { BarChartWidget, DonutChartWidget } from '@/components/advanced/Widgets';
import { useCallCentreAdminService } from '@actschurch/ccms-sdk';
import { useCallCentrePermissions } from '@/hooks/callcentre/useCallCentrePermissions';
import {
  CallCentrePageHeader,
  StatTile,
  StatTilesSkeleton,
  SectionCard,
  InlineEmpty,
  MeterBar,
  PersonCell,
  CampaignStatusBadge,
  callCentreKeys,
  callCentrePaths,
  callCentreErrorMessage,
  cssColour,
  plural,
  relativeStamp,
  formatYmd,
  ymdToDate,
  useCallCentreAiAvailability,
  CallCentreLiveCard,
} from '../components';
import { AiThemesCard } from './AiThemesCard';
import { RemindersPanel } from './RemindersPanel';

const CHART_H = 280;

function ChartPlaceholder({ title, subtitle, text }: { title: string; subtitle?: string; text: string }) {
  return (
    <SectionCard title={title} description={subtitle}>
      <InlineEmpty icon={<IconPhone size={22} />} title="Nothing to show yet" description={text} />
    </SectionCard>
  );
}

/** "2026-10-12" to "12 Oct". */
const shortDay = (ymd: string) => {
  const d = ymdToDate(ymd);
  return d ? d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) : ymd;
};

export default function CallCentreDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const admin = useCallCentreAdminService();
  const perms = useCallCentrePermissions();
  const aiAvailability = useCallCentreAiAvailability();
  const showThemes = aiAvailability.insights && perms.canUseAi;

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: callCentreKeys.dashboard(),
    queryFn: () => admin.getDashboard(),
    refetchInterval: 120_000,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: callCentreKeys.dashboard() });
    queryClient.invalidateQueries({ queryKey: callCentreKeys.remindersRoot() });
  };

  const byDay = (data?.callsByDay ?? []).map((d) => ({ day: shortDay(d.date), calls: d.calls, reached: d.reached }));
  const funnel = (data?.funnel ?? []).map((f) => ({ step: f.code, completed: f.completed, booked: f.booked, noShow: f.noShow }));
  const outcomes = (data?.outcomesThisWeek ?? []).filter((o) => o.count > 0).map((o) => ({ name: o.name, value: o.count, color: cssColour(o.color, 'blue') }));
  const outcomeTotal = outcomes.reduce((n, o) => n + o.value, 0);

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          <CallCentrePageHeader
            title="Call campaigns"
            description="The organisation's call centre: who is being called, how the calling is going and what needs following up."
            helpKey="callcentre.dashboard"
            actions={
              <>
                <ActionIcon variant="subtle" size="lg" onClick={refresh} loading={isFetching && !isLoading} aria-label="Refresh">
                  <IconRefresh size={18} />
                </ActionIcon>
                {perms.canManageCampaigns && (
                  <Button leftSection={<IconPlus size={16} />} onClick={() => navigate(callCentrePaths.newCampaign)} data-help-id="callcentre.dashboard.new">
                    New campaign
                  </Button>
                )}
              </>
            }
          />

          {error && <Alert color="red">{callCentreErrorMessage(error, "Couldn't load the dashboard.")}</Alert>}

          {isLoading ? (
            <Stack gap="md">
              <StatTilesSkeleton count={4} />
              <StatTilesSkeleton count={4} />
              <Skeleton height={300} radius="md" />
            </Stack>
          ) : data ? (
            <>
              <SimpleGrid cols={{ base: 2, md: 4 }} data-help-id="callcentre.dashboard.stats">
                <StatTile icon={<IconPhoneCall size={22} />} color="green" label="Active campaigns" value={data.activeCampaigns} hint={plural(data.activeCallersThisWeek, 'caller') + ' active this week'} onClick={() => navigate(`${callCentrePaths.campaigns}?status=2`)} helpId="callcentre.dashboard.stat-campaigns" />
                <StatTile icon={<IconUsers size={22} />} color="blue" label="Open contacts" value={data.openContacts} hint={`${data.callbacksDueToday} callbacks due today`} onClick={() => navigate(`${callCentrePaths.contacts}?open=1`)} />
                <StatTile icon={<IconUserQuestion size={22} />} color="orange" label="Not assigned" value={data.unassignedContacts} emphasis={data.unassignedContacts > 0} hint="Waiting for a caller" onClick={() => navigate(`${callCentrePaths.contacts}?assigned=unassigned`)} helpId="callcentre.dashboard.stat-unassigned" />
                <StatTile icon={<IconPhone size={22} />} color="indigo" label="Calls today" value={data.callsToday} hint={`${data.callsThisWeek} this week`} onClick={() => navigate(callCentrePaths.reports)} />
              </SimpleGrid>
              <SimpleGrid cols={{ base: 2, md: 4 }}>
                <StatTile icon={<IconTargetArrow size={22} />} color="teal" label="Reach rate (week)" value={`${Math.round(data.reachRatePercentThisWeek)}%`} hint={`${data.reachedThisWeek} reached this week`} onClick={() => navigate(callCentrePaths.reports)} />
                <StatTile icon={<IconArrowForwardUp size={22} />} color="grape" label="Open handoffs" value={data.openHandoffs} emphasis={data.openHandoffs > 0} hint="Care and ministry follow-up" onClick={() => navigate(callCentrePaths.handoffs())} helpId="callcentre.dashboard.stat-handoffs" />
                <StatTile icon={<IconListDetails size={22} />} color="yellow" label="Detail changes" value={data.pendingDetailChanges} emphasis={data.pendingDetailChanges > 0} hint="Waiting for review" onClick={() => navigate(callCentrePaths.detailChanges)} />
                <StatTile icon={<IconFileImport size={22} />} color="cyan" label="Lists waiting" value={data.importsWaiting} emphasis={data.importsWaiting > 0} hint="Matches to review" onClick={() => navigate(callCentrePaths.campaigns)} />
              </SimpleGrid>

              <CallCentreLiveCard helpId="callcentre.dashboard.live" />

              <Grid gutter="lg">
                <Grid.Col span={{ base: 12, lg: 7 }}>
                  <SectionCard
                    icon={<IconPhoneCall size={18} />}
                    color="green"
                    title="Active campaigns"
                    description="How far each live campaign has got."
                    helpId="callcentre.dashboard.campaigns"
                    actions={
                      <Button variant="subtle" size="compact-sm" rightSection={<IconChevronRight size={14} />} onClick={() => navigate(callCentrePaths.campaigns)}>
                        All campaigns
                      </Button>
                    }
                  >
                    {data.campaigns.length === 0 ? (
                      <InlineEmpty
                        icon={<IconPhoneCall size={22} />}
                        title="No live campaigns"
                        description="Create a campaign, add people, share them out to callers and launch it."
                        action={
                          perms.canManageCampaigns ? (
                            <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => navigate(callCentrePaths.newCampaign)}>
                              New campaign
                            </Button>
                          ) : undefined
                        }
                      />
                    ) : (
                      <Stack gap="xs">
                        {data.campaigns.map((c) => (
                          <Paper key={c.campaignID} withBorder radius="md" p="sm" style={{ cursor: 'pointer' }} onClick={() => navigate(callCentrePaths.campaign(c.campaignID))}>
                            <Stack gap={6}>
                              <Group justify="space-between" wrap="nowrap" gap="sm">
                                <Stack gap={0} style={{ minWidth: 0 }}>
                                  <Text size="sm" fw={600} lineClamp={1}>
                                    {c.name}
                                  </Text>
                                  <Text size="xs" c="dimmed" lineClamp={1}>
                                    {[c.campaignTypeName, plural(c.callerCount, 'caller'), c.endDate ? `ends ${formatYmd(c.endDate)}` : null].filter(Boolean).join(' · ')}
                                  </Text>
                                </Stack>
                                <Group gap="xs" wrap="nowrap">
                                  <CampaignStatusBadge statusId={c.statusID} name={c.statusName} color={c.statusColor} />
                                  <Text size="sm" fw={700}>
                                    {Math.round(c.counts.progressPercent)}%
                                  </Text>
                                </Group>
                              </Group>
                              <MeterBar value={c.counts.progressPercent} color="green" />
                              <Text size="xs" c="dimmed">
                                {c.counts.open} open · {c.counts.unassigned} not assigned · {c.counts.callbackDue} callbacks due · {c.counts.reached} reached of {c.counts.total}
                              </Text>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 5 }}>
                  <RemindersPanel />
                </Grid.Col>

                <Grid.Col span={{ base: 12, lg: 7 }}>
                  <div data-help-id="callcentre.dashboard.calls-by-day">
                    {byDay.some((d) => d.calls > 0) ? (
                      <BarChartWidget title="Calls per day" subtitle="The last 14 days: calls made and people reached" data={byDay} xAxisKey="day" height={CHART_H} showGrid showLegend showExport={false}
                        bars={[{ dataKey: 'calls', name: 'Calls', color: 'var(--mantine-color-blue-6)' }, { dataKey: 'reached', name: 'Reached', color: 'var(--mantine-color-green-6)' }]} />
                    ) : (
                      <ChartPlaceholder title="Calls per day" subtitle="The last 14 days" text="No calls logged in the last 14 days." />
                    )}
                  </div>
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 5 }}>
                  <div data-help-id="callcentre.dashboard.outcomes">
                    {outcomes.length ? (
                      <DonutChartWidget title="Outcomes this week" subtitle="What happened on each call" data={outcomes} height={CHART_H} showLegend showPercentages showExport={false} centerLabel={`${outcomeTotal}`} />
                    ) : (
                      <ChartPlaceholder title="Outcomes this week" text="No call outcomes logged this week." />
                    )}
                  </div>
                </Grid.Col>

                <Grid.Col span={{ base: 12, lg: 7 }}>
                  <div data-help-id="callcentre.dashboard.funnel">
                    {funnel.length ? (
                      <BarChartWidget
                        title="Journey funnel"
                        subtitle="People per step: completed, booked and no-shows"
                        data={funnel}
                        xAxisKey="step"
                        height={CHART_H}
                        showGrid
                        showLegend
                        showExport={false}
                        headerActions={
                          <Button variant="subtle" size="compact-sm" rightSection={<IconChevronRight size={14} />} onClick={() => navigate(callCentrePaths.journey())}>
                            Journey
                          </Button>
                        }
                        bars={[
                          { dataKey: 'completed', name: 'Completed', color: 'var(--mantine-color-teal-6)' },
                          { dataKey: 'booked', name: 'Booked', color: 'var(--mantine-color-blue-5)' },
                          { dataKey: 'noShow', name: 'No-show', color: 'var(--mantine-color-orange-5)' },
                        ]}
                      />
                    ) : (
                      <ChartPlaceholder title="Journey funnel" text="No journey steps are set up yet." />
                    )}
                  </div>
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 5 }}>
                  <SectionCard
                    icon={<IconTrophy size={18} />}
                    color="yellow"
                    title="Top callers"
                    description="This week, by calls made."
                    helpId="callcentre.dashboard.top-callers"
                    actions={
                      <Button variant="subtle" size="compact-sm" rightSection={<IconChevronRight size={14} />} onClick={() => navigate(callCentrePaths.callers)}>
                        Callers
                      </Button>
                    }
                  >
                    {data.topCallers.length === 0 ? (
                      <InlineEmpty icon={<IconUsers size={22} />} title="No calls this week" description="Callers show here once they log calls." />
                    ) : (
                      <Stack gap={8}>
                        {data.topCallers.map((c, i) => (
                          <Group key={c.userId} justify="space-between" wrap="nowrap" gap="sm">
                            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                              <Text size="sm" c="dimmed" w={18}>
                                {i + 1}
                              </Text>
                              <PersonCell name={c.displayName} secondary={c.lastCallAt ? `Last call ${relativeStamp(c.lastCallAt)}` : undefined} size="sm" avatarKey={c.userId} />
                            </Group>
                            <Group gap={6} wrap="nowrap">
                              <Badge variant="light" color="blue" styles={{ root: { textTransform: 'none' } }}>
                                {plural(c.calls, 'call')}
                              </Badge>
                              <Badge variant="light" color="green" styles={{ root: { textTransform: 'none' } }}>
                                {c.reached} reached
                              </Badge>
                            </Group>
                          </Group>
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                </Grid.Col>

                {showThemes && (
                  <Grid.Col span={12}>
                    <AiThemesCard />
                  </Grid.Col>
                )}
              </Grid>
              <Group gap={6}>
                <IconClock size={14} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  The numbers refresh every two minutes; the Live card updates as calls happen.
                </Text>
              </Group>
            </>
          ) : null}
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}
